import { DialogComponent } from '@theme/dialog';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { PersonalizationUpdateEvent } from '@theme/events';
import { isProfane } from '@theme/profanity-filter';
import { debounce } from '@theme/utilities';

/**
 * @typedef {Object} PersonalizedVariant
 * @property {number} id - The personalized variant bought in place of the selected one
 * @property {number} price - Its price in minor units
 * @property {number} compareAtPrice - Its compare-at price in minor units, 0 when it has none
 * @property {string} model - The first option value, lowercased, without spaces
 * @property {string} swatch - The last option value, lowercased, spaces as underscores
 * @property {string} image - The preview image URL
 * @property {string} color - The preview colour, as a hex value
 */

/**
 * @typedef {Object} Slot
 * @property {string} text
 * @property {string} icon
 */

/**
 * @typedef {Object} ProductPersonalizationRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} checkbox
 * @property {HTMLInputElement} textInput
 * @property {HTMLImageElement} previewImage
 * @property {HTMLElement} preview
 * @property {HTMLScriptElement} variantMap
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} saveButton
 * @property {HTMLElement} announcement
 * @property {HTMLElement} dialogAnnouncement
 * @property {HTMLElement[]} marks
 * @property {HTMLElement[]} markTexts
 * @property {HTMLElement[]} [markIcons]
 * @property {HTMLElement[]} summaryItems
 * @property {HTMLInputElement[]} [iconInputs]
 * @property {HTMLElement} [counter]
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 */

/** Characters each personalization method can produce; anything else is dropped as it is typed. */
const ALLOWED_CHARACTERS = {
  uv_printing: /^[ ~`!@#$%^&*()_+=[\]\{}|;':",.\/<>?a-zA-Z0-9-]+$/,
  embroidery: /^[a-zA-Z0-9 !"%'&(),\.\-:;`]+$/,
};

/**
 * Collects a personalization for each item of the product — text, and for UV printing an icon — and previews it on
 * the personalized variant's image. The dialog edits a draft: Save commits it, Clear drops the draft and the saved
 * personalization both. A saved personalization reaches the cart through `applyTo`, which the product form calls,
 * and a `PersonalizationUpdateEvent` tells the page whether it applies to the selected variant.
 * A variant change keeps what was entered and hides the offer while the variant has no personalized counterpart, or
 * leaves it inert while the variant cannot be bought.
 *
 * @extends {DialogComponent}
 */
class ProductPersonalization extends DialogComponent {
  requiredRefs = ['dialog', 'checkbox', 'textInput', 'saveButton', 'previewImage', 'preview', 'variantMap'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Record<string, string>} Each variant's model and swatch key. */
  #variantKeys = {};

  /** @type {Record<string, PersonalizedVariant>} Personalized variants, by model and swatch key. */
  #personalizedVariants = {};

  /** @type {Slot[]} */
  #draft = [];

  /** @type {Slot[]} The committed personalization, filled slots first. Empty while nothing is saved. */
  #saved = [];

  #slotIndex = 0;

  #processTextDebounced = debounce(() => this.#processText(), 100);

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const { variants = {}, personalized = {} } = JSON.parse(this.refs.variantMap.textContent || '{}');
    this.#variantKeys = variants;
    this.#personalizedVariants = personalized;
    this.#draft = this.#emptySlots();

    const container = this.closest('.shopify-section, dialog');
    container?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#processTextDebounced.cancel();
  }

  /** @returns {string} */
  get #type() {
    return this.dataset.type ?? '';
  }

  /** @returns {number} */
  get #slotCount() {
    return Number(this.dataset.slotCount) || 1;
  }

  /**
   * Buys the personalized variant with the saved personalization in place of the variant the form data holds.
   * Leaves the data untouched while nothing is saved or the variant has no personalized counterpart.
   * @param {FormData} formData
   */
  applyTo(formData) {
    if (this.#saved.length === 0) return;

    const variant = this.#personalizedVariantFor(formData.get('id'));
    if (!variant) return;

    /** @type {Record<string, unknown>} */
    const personalization = {
      _pr_item_description: `${this.#type} ${variant.model} ${variant.swatch}`,
    };

    this.#saved.forEach((slot, index) => {
      const slotNumber = index + 1;

      formData.set(`properties[p${slotNumber}]`, this.#summarize(slot));
      personalization[`_pr_db_${slotNumber}`] = {
        type: this.#type,
        size: variant.model,
        color: variant.swatch,
        print_color: variant.color.replace('#', '').toUpperCase(),
        print_text: slot.text,
        print_icon: slot.icon,
        template_name: `${variant.model}_${variant.swatch}`,
      };
    });

    formData.set('id', String(variant.id));
    formData.set('properties[_personalization]', JSON.stringify(personalization));
  }

  /**
   * Opens the dialog on the first item, showing the saved personalization: edits closed without Save are dropped.
   */
  open() {
    this.#restoreDraft();
    this.#selectSlot(0);
    this.showDialog();
  }

  /**
   * Opens the dialog from the tile, leaving the checkbox as it is: only Save ticks it.
   * @param {MouseEvent} event
   */
  edit(event) {
    event.preventDefault();
    this.open();
  }

  /**
   * Removes the saved personalization from a ticked tile's check mark, or opens the dialog from an empty one.
   */
  removePersonalization() {
    if (this.#saved.length === 0) {
      this.open();
      return;
    }

    this.#reset();
    this.#announce(this.refs.announcement, this.dataset.removedAnnouncement);
  }

  showPreviousSlot() {
    this.#flushText();
    this.#selectSlot(this.#slotIndex - 1, true);
  }

  showNextSlot() {
    this.#flushText();
    this.#selectSlot(this.#slotIndex + 1, true);
  }

  handleTextInput() {
    this.#processTextDebounced();
  }

  /**
   * Unticks the icon when the one already chosen is clicked again. The click fires before `change`, so the slot
   * still holds the previous choice here.
   * @param {MouseEvent} event
   */
  handleIconClick(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const slot = this.#draft[this.#slotIndex];
    if (!slot) return;

    if (slot.icon === input.value) {
      input.checked = false;
      this.#setIcon('');
    } else {
      this.#setIcon(input.value);
    }
  }

  /**
   * Follows an icon chosen with the arrow keys, which changes the selection without a click on it.
   * @param {Event} event
   */
  handleIconChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    this.#setIcon(input.value);
  }

  /**
   * Commits the draft, filled items first, and closes the dialog. A draft with nothing in it removes the personalization.
   * @param {SubmitEvent} event
   */
  save(event) {
    event.preventDefault();

    this.#flushText();

    // Enter within the debounce, or a click on the aria-disabled button, still submits; it lands on the item at fault
    const profaneSlotIndex = this.#draft.findIndex((slot) => isProfane(slot.text));
    if (profaneSlotIndex !== -1) {
      this.#selectSlot(profaneSlotIndex, profaneSlotIndex !== this.#slotIndex);
      return;
    }

    const filledSlots = this.#draft.filter((slot) => this.#isFilled(slot));

    if (filledSlots.length === 0) {
      this.#reset();
      this.closeDialog();
      return;
    }

    this.#saved = filledSlots.map((slot) => ({ ...slot }));
    this.#restoreDraft();
    this.#renderSummary();

    this.refs.checkbox.checked = true;
    this.toggleAttribute('data-saved', true);
    this.#dispatchUpdate();

    // The open modal leaves the page's live region inert
    this.closeDialog().then(() => this.#announce(this.refs.announcement, this.dataset.savedAnnouncement));
  }

  /**
   * Drops the draft and the saved personalization, keeping the dialog open.
   */
  clear() {
    this.#reset();
    this.#selectSlot(0);
    this.#announce(this.refs.dialogAnnouncement, this.dataset.clearedAnnouncement);
  }

  /**
   * Moves the offer to the selected variant's personalized counterpart, or hides it when there is none.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        // A morphed `<main>`, for a combined listing sibling, brings its own element
        const variantId = detail?.resource?.id;
        if (!this.isConnected || detail?.morphElementSelector || !variantId) return;

        const variant = this.#personalizedVariantFor(variantId);
        this.hidden = !variant;
        this.#dispatchUpdate(variantId);
        if (!variant) return;

        const rendered = detail.html?.querySelector(`product-personalization[data-form-id="${this.dataset.formId}"]`);
        this.inert = rendered?.hasAttribute('inert') ?? false;

        if (variant.image) {
          this.refs.previewImage.src = variant.image;
        } else {
          this.refs.previewImage.removeAttribute('src');
        }
        this.refs.preview.style.setProperty('--personalization-color', variant.color);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[product-personalization] Event promise rejected:', error);
      });
  };

  /**
   * Processes text still waiting on the debounce, so it lands in the item it was typed for.
   */
  #flushText() {
    this.#processTextDebounced.cancel();
    this.#processText();
  }

  /**
   * Drops characters the method cannot produce and flags text that holds a profanity, which blocks Save, then
   * previews the result.
   */
  #processText() {
    const { textInput } = this.refs;
    const slot = this.#draft[this.#slotIndex];
    if (!slot) return;

    const allowedCharacters = ALLOWED_CHARACTERS[/** @type {keyof typeof ALLOWED_CHARACTERS} */ (this.#type)];
    const enteredText = textInput.value;
    let text = allowedCharacters
      ? Array.from(enteredText)
          .filter((character) => allowedCharacters.test(character))
          .join('')
      : enteredText;

    if (isProfane(text)) {
      this.#showError(this.dataset.profanityError);
    } else if (text !== enteredText) {
      this.#showError(this.dataset.charactersError);
    } else {
      this.#hideError();
    }

    if (textInput.value !== text) textInput.value = text;

    slot.text = text;
    this.#renderMarks();
    this.#updateSaveButton();
  }

  /**
   * @param {number} index
   * @param {boolean} [announce] - Whether to announce the item now shown
   */
  #selectSlot(index, announce = false) {
    const { textInput, iconInputs = [], counter, previousButton, nextButton, marks } = this.refs;
    const slotCount = this.#slotCount;

    this.#slotIndex = Math.min(Math.max(index, 0), slotCount - 1);
    const slot = this.#draft[this.#slotIndex] ?? { text: '', icon: '' };

    textInput.value = slot.text;
    if (isProfane(slot.text)) {
      this.#showError(this.dataset.profanityError);
    } else {
      this.#hideError();
    }

    for (const input of iconInputs) {
      input.checked = input.value === slot.icon;
    }

    marks.forEach((mark, markIndex) => mark.toggleAttribute('data-active', markIndex === this.#slotIndex));

    if (counter) counter.textContent = `${this.#slotIndex + 1} / ${slotCount}`;
    if (previousButton) previousButton.disabled = this.#slotIndex === 0;
    if (nextButton) nextButton.disabled = this.#slotIndex === slotCount - 1;

    if (announce) {
      this.#announce(
        this.refs.dialogAnnouncement,
        this.dataset.slotAnnouncement
          ?.replace('[current]', String(this.#slotIndex + 1))
          .replace('[total]', String(slotCount))
      );
    }
  }

  /** @param {string} icon */
  #setIcon(icon) {
    const slot = this.#draft[this.#slotIndex];
    if (!slot) return;

    slot.icon = icon;
    this.#renderMarks();
  }

  /**
   * Draws each item's text and icon on the preview.
   */
  #renderMarks() {
    const { markTexts, markIcons = [], iconInputs = [] } = this.refs;

    this.#draft.forEach((slot, index) => {
      const markText = markTexts[index];
      if (markText) markText.textContent = slot.text;

      const markIcon = markIcons[index];
      if (!markIcon) return;

      const iconInput = iconInputs.find((input) => input.value === slot.icon);
      const iconImage = iconInput?.closest('label')?.querySelector('svg');
      markIcon.replaceChildren(...(iconImage ? [iconImage.cloneNode(true)] : []));
    });
  }

  /**
   * Lists the saved items on the tile, numbered when the product has more than one.
   */
  #renderSummary() {
    const numbered = this.#slotCount > 1;

    this.refs.summaryItems.forEach((item, index) => {
      const slot = this.#saved[index];
      const summary = slot ? this.#summarize(slot) : '';

      item.textContent = summary && numbered ? `${index + 1} - ${summary}` : summary;
    });
  }

  /**
   * Replaces the draft with the saved personalization, padded with empty items.
   */
  #restoreDraft() {
    this.#draft = [...this.#saved.map((slot) => ({ ...slot })), ...this.#emptySlots()].slice(0, this.#slotCount);
    this.#renderMarks();
    this.#updateSaveButton();
  }

  /**
   * Blocks Save while any item holds a profanity.
   */
  #updateSaveButton() {
    const { saveButton } = this.refs;

    if (this.#hasProfanity) {
      saveButton.setAttribute('aria-disabled', 'true');
    } else {
      saveButton.removeAttribute('aria-disabled');
    }
  }

  /** @returns {boolean} */
  get #hasProfanity() {
    return this.#draft.some((slot) => isProfane(slot.text));
  }

  /**
   * Tells the page whether a saved personalization applies to the selected variant, and the personalized variant's
   * prices while it does.
   * @param {unknown} [variantId] - The selected variant; defaults to the one the product form holds.
   */
  #dispatchUpdate(variantId = this.#formVariantId) {
    const variant = this.#saved.length > 0 && !this.hidden ? this.#personalizedVariantFor(variantId) : undefined;

    this.dispatchEvent(
      new PersonalizationUpdateEvent(
        variant ? this.dataset.summaryLabel ?? null : null,
        variant ? { price: variant.price, compareAtPrice: variant.compareAtPrice } : null
      )
    );
  }

  /** @returns {string | undefined} */
  get #formVariantId() {
    const form = document.getElementById(this.dataset.formId ?? '');
    const input = form instanceof HTMLFormElement ? form.elements.namedItem('id') : null;
    return input instanceof HTMLInputElement ? input.value : undefined;
  }

  #reset() {
    this.#saved = [];
    this.#restoreDraft();
    this.#renderSummary();

    this.refs.checkbox.checked = false;
    this.removeAttribute('data-saved');
    this.#dispatchUpdate();
  }

  /**
   * The line item property the cart shows for an item. Its wording is read by order processing, so it stays as is.
   * @param {Slot} slot
   * @returns {string}
   */
  #summarize(slot) {
    const icon = slot.icon ? `${slot.icon} icon` : '';

    if (slot.text && icon) return `${slot.text} / ${icon}`;

    return slot.text || icon;
  }

  /**
   * @param {Slot} slot
   * @returns {boolean}
   */
  #isFilled(slot) {
    if (slot.text.trim() !== '') return true;

    return this.#type === 'uv_printing' && slot.icon !== '';
  }

  /**
   * The personalized variant standing in for a variant, when it has one.
   * @param {unknown} variantId
   * @returns {PersonalizedVariant | undefined}
   */
  #personalizedVariantFor(variantId) {
    const key = this.#variantKeys[String(variantId)];
    if (!key) return undefined;

    return this.#personalizedVariants[key];
  }

  /** @returns {Slot[]} */
  #emptySlots() {
    return Array.from({ length: this.#slotCount }, () => ({ text: '', icon: '' }));
  }

  /** @param {string} [message] */
  #showError(message) {
    const { error, textInput } = this.refs;
    if (!message) return;

    error.textContent = message;
    error.hidden = false;
    textInput.setAttribute('aria-invalid', 'true');
  }

  #hideError() {
    const { error, textInput } = this.refs;

    error.textContent = '';
    error.hidden = true;
    textInput.removeAttribute('aria-invalid');
  }

  /**
   * @param {HTMLElement} region
   * @param {string} [message]
   */
  #announce(region, message) {
    if (!message) return;

    region.textContent = message;
  }
}

if (!customElements.get('product-personalization')) {
  customElements.define('product-personalization', ProductPersonalization);
}
